import {
  ExportTemplatePresetValue,
  TransformedData,
} from '@aibees/elephant/components/export-dialog';
import { ElephantPresetType } from '@aibees/elephant/types';
import { useBrowsePresetApi, useGetSinglePresetApi } from '@aibees/shared/api';
import { Maybe, ParsedPreset } from '@aibees/shared/definition';
import { OperationMode } from '@aibees/shared/features';
import { createContext, ReactNode, useContext, useMemo, useState } from 'react';
import { useExportTemplateDataType } from '../hooks/use-export-template-data-type';

interface TemplateContextProps {
  selectedTemplate: ParsedPreset<ExportTemplatePresetValue> | null;
  templateName: string;
  setTemplateName: (templateName: string) => void;
  setSelectedTemplate: (
    template: ParsedPreset<ExportTemplatePresetValue> | null
  ) => void;
  filteredItems: TransformedData | undefined;
  setFilteredItems: (template: TransformedData) => void;
  getTemplateById: (
    id: string
  ) => Maybe<ParsedPreset<ExportTemplatePresetValue>>;
  templates: ParsedPreset<ExportTemplatePresetValue>[];
  presetType: ElephantPresetType;
  templateMode: OperationMode;
  setTemplateMode: (templateMode: OperationMode) => void;
  selectedPreset: ParsedPreset<string> | null;
}

const TemplateContext = createContext<TemplateContextProps | undefined>(
  undefined
);

export const useTemplateContext = () => {
  const context = useContext(TemplateContext);
  if (!context) {
    throw new Error(
      'useTemplateContext must be used within a TemplateProvider'
    );
  }
  return context;
};

export const TemplateProvider = ({ children }: { children: ReactNode }) => {
  const [templateName, setTemplateName] = useState<string>('');
  const [selectedTemplate, setSelectedTemplate] =
    useState<ParsedPreset<ExportTemplatePresetValue> | null>(null);
  const [filteredItems, setFilteredItems] = useState<TransformedData>();
  const [templateMode, setTemplateMode] = useState<OperationMode>(
    OperationMode.CREATE
  );
  const presetType = useExportTemplateDataType();
  const presetApi = useBrowsePresetApi<ExportTemplatePresetValue>({
    type: presetType,
    size: 25,
  });
  const { data: selectedPreset } = useGetSinglePresetApi<string>({
    type: 'SELECTED_'.concat(presetType),
  });

  const getTemplateById = (id: string) => {
    return templates.find((x) => x.id === id);
  };

  const templates = useMemo(() => {
    return (presetApi.data?.content ??
      []) as ParsedPreset<ExportTemplatePresetValue>[];
  }, [presetApi.data?.content]);

  return (
    <TemplateContext.Provider
      value={{
        selectedTemplate,
        setSelectedTemplate,
        getTemplateById,
        templates,
        presetType,
        setFilteredItems,
        filteredItems,
        setTemplateName,
        templateName,
        templateMode,
        setTemplateMode,
        selectedPreset,
      }}
    >
      {children}
    </TemplateContext.Provider>
  );
};
